// src/pages/AcceptInvite.jsx
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Info, CheckCircle2, XCircle } from "lucide-react";
import { supabase } from "../../supabaseClient";
import { getHasBusinessForUser } from "../../hooks/useBusinessGate";

const PASSWORD_RULES = [
  { key: "length", label: "At least 8 characters", test: (s) => s.length >= 8 },
  { key: "upper", label: "At least 1 uppercase letter (A–Z)", test: (s) => /[A-Z]/.test(s) },
  { key: "lower", label: "At least 1 lowercase letter (a–z)", test: (s) => /[a-z]/.test(s) },
  { key: "number", label: "At least 1 number (0–9)", test: (s) => /\d/.test(s) },
  { key: "special", label: "At least 1 special character (!@#$…)", test: (s) => /[^A-Za-z0-9]/.test(s) },
];

export default function AcceptInvite() {
  const navigate = useNavigate();

  const params = useMemo(() => {
    return new URLSearchParams(window.location.search);
  }, []);

  const tokenHash = params.get("token_hash") || "";
  const type = params.get("type") || "invite";
  const emailFromUrl = params.get("email") || "";

  const [mode, setMode] = useState(tokenHash ? "link" : "otp");
  const [verified, setVerified] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    email: emailFromUrl,
    phone: "",
    otp: "",
    password: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const cleanEmail = (formData.email || "").trim().toLowerCase();
  const cleanOtp = (formData.otp || "").trim().replace(/\s+/g, "");

  function handleInputChange(field, value) {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }

  async function loadVerifiedUserEmail() {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (user?.email) {
      setFormData((prev) => ({
        ...prev,
        email: user.email,
      }));
    }

    return user;
  }

  async function acceptInviteWithLink() {
    if (!tokenHash) {
      setErrorMessage("This invite link is missing its verification token. Please use the invite code instead.");
      setMode("otp");
      return;
    }

    if (type !== "invite") {
      setErrorMessage("This invite link is not valid. Please use the invite code instead.");
      setMode("otp");
      return;
    }

    setLoading(true);
    setMessage("");
    setErrorMessage("");

    try {
      const { error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: "invite",
      });

      if (error) {
        console.error("Invite link verification error:", error);
        setErrorMessage("This invite link is invalid or expired. Please use the invite code instead.");
        setMode("otp");
        return;
      }

      await loadVerifiedUserEmail();

      setVerified(true);
      setMessage("Invite accepted. Please complete your details and create your password.");
    } catch (err) {
      console.error("Unexpected invite link verification error:", err);
      setErrorMessage("Something went wrong. Please use the invite code instead.");
      setMode("otp");
    } finally {
      setLoading(false);
    }
  }

  async function acceptInviteWithOtp() {
    if (!cleanEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }

    if (!cleanOtp) {
      setErrorMessage("Please enter the invite code from the email.");
      return;
    }

    setLoading(true);
    setMessage("");
    setErrorMessage("");

    try {
      const { error } = await supabase.auth.verifyOtp({
        email: cleanEmail,
        token: cleanOtp,
        type: "invite",
      });

      if (error) {
        console.error("Invite OTP verification error:", error);
        setErrorMessage("The invite code is invalid or expired. Please ask for a new invite.");
        return;
      }

      await loadVerifiedUserEmail();

      setVerified(true);
      setMessage("Invite accepted. Please complete your details and create your password.");
    } catch (err) {
      console.error("Unexpected invite OTP verification error:", err);
      setErrorMessage("Something went wrong while verifying the invite code.");
    } finally {
      setLoading(false);
    }
  }

  async function createAccount() {
    setMessage("");
    setErrorMessage("");

    const fullName = (formData.fullName || "").trim();
    const email = (formData.email || "").trim().toLowerCase();
    const phone = (formData.phone || "").trim();
    const password = formData.password || "";
    const confirmPassword = formData.confirmPassword || "";

    if (!fullName) {
      setErrorMessage("Please enter your full name.");
      return;
    }

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }

    if (!phone) {
      setErrorMessage("Please enter your phone number.");
      return;
    }

    const unmet = PASSWORD_RULES.filter((r) => !r.test(password));
    if (unmet.length > 0) {
      setErrorMessage("Password must meet all requirements shown below.");
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password,
        data: {
          full_name: fullName,
          mobile_number: phone,
          phone,
        },
      });

      if (error) {
        console.error("Account update error:", error);
        setErrorMessage("Could not save your account details. Please try again.");
        return;
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();

      const hasBiz = user?.id ? await getHasBusinessForUser(user.id) : false;

      navigate(hasBiz ? "/dashboard" : "/profile-gate", {
        replace: true,
      });
    } catch (err) {
      console.error("Unexpected account update error:", err);
      setErrorMessage("Something went wrong while creating your account.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#FFF7ED] flex items-center justify-center px-4 font-sans">
      <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl border border-orange-100">
        <div className="text-center mb-6">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-orange-100 text-3xl">
            ✉️
          </div>

          <h1 className="text-2xl font-extrabold text-gray-900 mb-2">
            Accept your invite
          </h1>

          <p className="text-gray-600 leading-relaxed">
            Create your Gwin account using your invite email.
          </p>
        </div>

        {message && (
          <div className="mb-4 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
            {message}
          </div>
        )}

        {errorMessage && (
          <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {errorMessage}
          </div>
        )}

        {!verified ? (
          <>
            {tokenHash && mode === "link" && (
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={acceptInviteWithLink}
                  disabled={loading}
                  className="w-full rounded-2xl bg-[#FF914D] px-4 py-3 font-bold text-white shadow-md disabled:opacity-60"
                >
                  {loading ? "Accepting invite..." : "Accept invite"}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setMode("otp");
                    setMessage("");
                    setErrorMessage("");
                  }}
                  className="w-full rounded-2xl border border-orange-200 bg-white px-4 py-3 font-bold text-[#FF914D]"
                >
                  Use invite code instead
                </button>
              </div>
            )}

            {mode === "otp" && (
              <div className="space-y-4">
                {InputField({
                  label: "Email address",
                  field: "email",
                  type: "email",
                  autoComplete: "email",
                  placeholder: "name@example.com",
                })}

                {InputField({
                  label: "Invite code",
                  field: "otp",
                  type: "text",
                  inputMode: "numeric",
                  autoComplete: "one-time-code",
                  placeholder: "6-digit code",
                  extraClass: "tracking-[0.3em]",
                })}

                <button
                  type="button"
                  onClick={acceptInviteWithOtp}
                  disabled={loading}
                  className="w-full rounded-2xl bg-[#FF914D] px-4 py-3 font-bold text-white shadow-md disabled:opacity-60"
                >
                  {loading ? "Verifying..." : "Verify invite code"}
                </button>

                {tokenHash && (
                  <button
                    type="button"
                    onClick={() => {
                      setMode("link");
                      setMessage("");
                      setErrorMessage("");
                    }}
                    className="w-full rounded-2xl border border-orange-200 bg-white px-4 py-3 font-bold text-[#FF914D]"
                  >
                    Use invite link instead
                  </button>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="space-y-4">
            {InputField({
              label: "Full Name",
              field: "fullName",
              autoComplete: "name",
              placeholder: "Full Name",
            })}

            {InputField({
              label: "Email",
              field: "email",
              type: "email",
              autoComplete: "email",
              placeholder: "Email",
              readOnly: true,
            })}

            {InputField({
              label: "Phone Number",
              field: "phone",
              type: "tel",
              autoComplete: "tel",
              placeholder: "Phone Number",
            })}

            {PasswordField({
              label: "Password",
              field: "password",
              show: showPassword,
              toggle: () => setShowPassword((prev) => !prev),
            })}

            <PasswordCriteria password={formData.password} />

            {PasswordField({
              label: "Confirm Password",
              field: "confirmPassword",
              show: showConfirmPassword,
              toggle: () => setShowConfirmPassword((prev) => !prev),
            })}

            <button
              type="button"
              onClick={createAccount}
              disabled={loading}
              className="w-full rounded-2xl bg-[#FF914D] px-4 py-3 font-bold text-white shadow-md disabled:opacity-60"
            >
              {loading ? "Creating account..." : "Create account"}
            </button>
          </div>
        )}

        <p className="mt-5 text-center text-xs text-gray-500">
          Only use the newest invite email. Old invite links may no longer work.
        </p>
      </div>
    </div>
  );

  function InputField({
    label,
    field,
    type = "text",
    inputMode,
    autoComplete = "off",
    placeholder,
    readOnly = false,
    extraClass = "",
  }) {
    return (
      <div className="space-y-2 text-left">
        <label className="block text-sm font-bold text-gray-700">
          {label}
        </label>
        <input
          type={type}
          inputMode={inputMode}
          value={formData[field]}
          onChange={(e) => handleInputChange(field, e.target.value)}
          placeholder={placeholder || label}
          autoComplete={autoComplete}
          readOnly={readOnly}
          className={`w-full rounded-2xl border border-gray-200 px-4 py-3 text-gray-900 outline-none focus:border-[#FF914D] ${
            readOnly ? "bg-gray-50 text-gray-500" : "bg-white"
          } ${extraClass}`}
        />
      </div>
    );
  }

  function PasswordField({ label, field, show, toggle }) {
    return (
      <div className="space-y-2 text-left">
        <label className="block text-sm font-bold text-gray-700">
          {label}
        </label>

        <div className="relative">
          <input
            type={show ? "text" : "password"}
            value={formData[field]}
            onChange={(e) => handleInputChange(field, e.target.value)}
            className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 pr-12 text-gray-900 outline-none focus:border-[#FF914D]"
            autoComplete="new-password"
            placeholder={label}
          />

          <button
            type="button"
            onClick={toggle}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
            aria-label={show ? "Hide password" : "Show password"}
          >
            <Eye className="w-5 h-5 text-gray-700" />
          </button>
        </div>
      </div>
    );
  }

  function PasswordCriteria({ password }) {
    return (
      <div
        role="region"
        aria-live="polite"
        className="rounded-2xl border border-gray-200 bg-gray-50 p-3 text-sm text-black/80"
      >
        <div className="flex items-center gap-2 mb-2">
          <Info className="w-4 h-4" />
          <span className="font-semibold">Your password must include:</span>
        </div>

        <ul className="space-y-1 pl-1">
          {PASSWORD_RULES.map((rule) => {
            const ok = rule.test(password || "");

            return (
              <li key={rule.key} className="flex items-center gap-2">
                {ok ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                ) : (
                  <XCircle className="w-4 h-4 text-gray-400" />
                )}

                <span className={ok ? "text-green-700" : "text-black/70"}>
                  {rule.label}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }
}